import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add as AddIcon,
    ContentCopy as CopyIcon,
    DeleteOutline as DeleteIcon,
    EditOutlined as EditIcon,
    OpenInNew as OpenInNewIcon,
    VpnKey as KeyIcon,
} from "@mui/icons-material";
import {useEffect, useState} from "react";
import ApiKeyDialog from "./ApiKeyDialog";
import {deleteApiKey, getApiKeysDocsUrl, listApiKeys} from "../../services/ApiKeyService";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {useAuth} from "../../contexts/AuthContext";
import {useCompany} from "../../contexts/CompanyContext";
import {StatusChip} from "../ui";
import {t} from "../dashboard/coach/coachTokens";

/* coach table presets */
const td = {padding: "10px 14px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 14px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const ApiKeysSection = () => {
    const {notificationHandler} = useMessageContext();
    const {isSuperAdmin} = useAuth();
    const {company} = useCompany();
    const companyId = company?.id;
    const needsCompanyChoice = isSuperAdmin() && !companyId;
    const [rows, setRows] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [docsUrl, setDocsUrl] = useState("");
    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => {
        if (needsCompanyChoice) {
            setRows([]);
            return;
        }
        listApiKeys(companyId)
            .then(res => setRows(res.data || []))
            .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
        getApiKeysDocsUrl()
            .then(res => setDocsUrl(res.data?.url || ""))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadTick, companyId, needsCompanyChoice]);

    const onCreate = () => {
        setEditing(null);
        setDialogOpen(true);
    };
    const onEdit = (row) => {
        setEditing(row);
        setDialogOpen(true);
    };
    const onDelete = async (row) => {
        // eslint-disable-next-line no-restricted-globals,no-alert
        if (!window.confirm(`האם למחוק את המפתח "${row.name}"?`)) return;
        try {
            await deleteApiKey(row.id);
            notificationHandler.success("המפתח נמחק");
            setReloadTick(t => t + 1);
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
        }
    };
    const onCopy = (row) => {
        navigator.clipboard.writeText(row.keyValue).catch(() => {});
        notificationHandler.success("הועתק");
    };

    return (
        <Paper sx={{backgroundColor: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh, overflow: "hidden", direction: "rtl"}}
               elevation={0}>
            {/* Header band */}
            <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 18px", backgroundColor: t.bg, borderBottom: `1px solid ${t.border}`,
            }}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                    <Box sx={{width: 36, height: 36, borderRadius: "10px", background: t.white, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <KeyIcon sx={{color: t.t2, fontSize: 20}}/>
                    </Box>
                    <Box>
                        <Typography sx={{fontWeight: 800, fontSize: 15, color: t.text, letterSpacing: "-0.2px"}}>מפתחות API</Typography>
                        <Typography sx={{fontSize: 11.5, color: t.t3, fontWeight: 600}}>{rows.length} מפתחות</Typography>
                    </Box>
                </Box>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    {docsUrl && (
                        <Box component="button" onClick={() => window.open(docsUrl, "_blank", "noopener")} sx={{
                            display: "inline-flex", alignItems: "center", gap: "5px",
                            background: t.white, color: t.t2, height: 34, px: "13px", borderRadius: t.rs,
                            fontSize: "12.5px", fontWeight: 600, border: `1px solid ${t.border}`, cursor: "pointer", fontFamily: "inherit",
                            transition: "all .14s", "&:hover": {background: t.greenLight, borderColor: t.green, color: t.green},
                        }}>
                            תיעוד API
                            <OpenInNewIcon sx={{fontSize: 15}}/>
                        </Box>
                    )}
                    <Box component="button" onClick={onCreate} disabled={needsCompanyChoice} sx={{
                        display: "inline-flex", alignItems: "center", gap: "5px",
                        background: t.green, color: "#fff", height: 34, px: "15px", borderRadius: t.rs,
                        fontSize: "13px", fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
                        transition: "all .14s",
                        "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                        "&:disabled": {background: t.border, color: t.t3, cursor: "not-allowed", transform: "none", boxShadow: "none"},
                    }}>
                        <AddIcon sx={{fontSize: 16}}/>
                        מפתח חדש
                    </Box>
                </Box>
            </Box>

            {/* Body */}
            {needsCompanyChoice ? (
                <Box sx={{padding: "48px", textAlign: "center"}}>
                    <Typography sx={{color: t.t3, fontSize: "13px"}}>בחר חברה ספציפית כדי לנהל מפתחות API.</Typography>
                </Box>
            ) : rows.length === 0 ? (
                <Box sx={{padding: "48px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 1}}>
                    <KeyIcon sx={{fontSize: 44, color: t.border}}/>
                    <Typography sx={{color: t.t3, fontSize: "13px", maxWidth: 360}}>
                        עדיין לא נוצרו מפתחות. צור כדי לאפשר למערכת חיצונית לנהל משתמשים תחת החברה שלך.
                    </Typography>
                </Box>
            ) : (
                <TableContainer sx={{overflowX: "auto"}}>
                    <Table sx={{tableLayout: "auto"}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{...th, textAlign: "right"}}>שם</TableCell>
                                <TableCell sx={{...th, textAlign: "right"}}>מפתח</TableCell>
                                <TableCell sx={{...th, textAlign: "center"}}>נוצר</TableCell>
                                <TableCell sx={{...th, textAlign: "center"}}>שימוש אחרון</TableCell>
                                <TableCell sx={{...th, textAlign: "center"}}>סטטוס</TableCell>
                                <TableCell sx={{...th, textAlign: "center", width: 120}}>פעולות</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.id} sx={{"&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                                    <TableCell sx={{...td, textAlign: "right", fontWeight: 600}}>{row.name}</TableCell>
                                    <TableCell sx={{...td, textAlign: "right", fontFamily: "monospace", fontSize: "12px", color: t.t2}}>
                                        {row.keyValue}
                                    </TableCell>
                                    <TableCell sx={{...td, textAlign: "center", color: t.t2}}>{row.createdDate ? new Date(row.createdDate).toLocaleDateString("he-IL") : "—"}</TableCell>
                                    <TableCell sx={{...td, textAlign: "center", color: t.t2}}>{row.lastUsedAt ? new Date(row.lastUsedAt).toLocaleString("he-IL") : "—"}</TableCell>
                                    <TableCell sx={{...td, textAlign: "center"}}>
                                        <StatusChip label={row.enabled ? "פעיל" : "כבוי"} variant={row.enabled ? "success" : "default"} size="small"/>
                                    </TableCell>
                                    <TableCell sx={{...td, textAlign: "center"}}>
                                        <Box sx={{display: "inline-flex", justifyContent: "center", gap: "2px"}}>
                                            <Tooltip title="העתק">
                                                <IconButton size="small" onClick={() => onCopy(row)} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                                    <CopyIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="עריכה">
                                                <IconButton size="small" onClick={() => onEdit(row)} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="מחיקה">
                                                <IconButton size="small" onClick={() => onDelete(row)} sx={{color: t.red, "&:hover": {bgcolor: t.redLight}}}>
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ApiKeyDialog
                open={dialogOpen}
                apiKey={editing}
                companyId={companyId}
                onClose={() => setDialogOpen(false)}
                onSaved={() => setReloadTick(t => t + 1)}
            />
        </Paper>
    );
};

export default ApiKeysSection;
